import * as Dialog from '@radix-ui/react-dialog';
import { CalendarX, Eye } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { Label } from '@/components/ui/field';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  JOB_REQUEST_TONES,
  useApproveChangeRequest,
  useChangeRequests,
  useDeclineChangeRequest,
} from '@/hooks/job-requests';
import { ApiError } from '@/lib/api';
import { formatDay, formatInstant, formatWallTime } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import type { AdminChangeRequestView } from '@shared/schemas/change-request';

/*
 * Reschedule and cancellation requests, in the same queue screen as the new
 * ones — ROADMAP.md §5.6.
 *
 * **Not a new sidebar item**, deliberately. design-system.md §6 preserves the
 * prototype's nav because the people who will use this have been using it, and
 * "what is waiting on me" is one question with one answer. So this is a second
 * section on Job Requests rather than a seventh row under OPERATIONS.
 *
 * **Confirming here moves the job.** The client's request did not, and would
 * not — that is question 12's answer, and the reason a crew already on its way
 * cannot be redirected by a customer with a phone.
 */

export function ChangeRequestQueue() {
  const [showAll, setShowAll] = useState(false);
  const requests = useChangeRequests({ page: 1, status: showAll ? 'all' : 'pending' });
  const [reviewing, setReviewing] = useState<{
    request: AdminChangeRequestView;
    action: 'approve' | 'decline';
  } | null>(null);

  const rows = requests.data?.data ?? [];
  const pending = requests.data?.counts.pending ?? 0;

  return (
    <section className="mb-8">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-h2 text-ink-900">
          Changes to booked cleans
          {pending > 0 ? (
            <span className="ml-2 rounded-full bg-honey-400 px-2 py-0.5 text-meta font-medium text-ink-900">
              {pending}
            </span>
          ) : null}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setShowAll((current) => !current);
          }}
        >
          {showAll ? 'Only what is waiting' : 'Show answered ones too'}
        </Button>
      </div>

      {requests.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            onRetry={() => {
              void requests.refetch();
            }}
          />
        </div>
      ) : requests.isPending ? (
        <div aria-hidden="true" className="h-24 animate-pulse rounded-lg bg-ink-100" />
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={CalendarX}
            title={showAll ? 'Nobody has asked to change a clean.' : 'Nothing waiting on you.'}
            hint="When a client asks to move or cancel a booked clean, it lands here. Their asking does not move it — you do."
          />
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((entry) => (
            <li
              key={entry.id}
              className="rounded-lg border border-ink-300 bg-white p-4 shadow-card"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="text-h3 text-ink-900">{entry.clientName}</span>
                    {/* The display value, never `cancellation` (P5). */}
                    <StatusBadge label={entry.kindLabel} tone="priority" />
                    <StatusBadge label={entry.statusLabel} tone={JOB_REQUEST_TONES[entry.status]} />
                  </p>
                  <p className="mt-0.5 text-meta text-ink-500">
                    Asked {formatInstant(entry.submittedAt)}
                    {entry.clientPhone ? ` · ${entry.clientPhone}` : ''}
                  </p>

                  <p className="mt-2 text-body text-ink-700">
                    <span className="text-ink-500">{entry.jobNumber}</span> ·{' '}
                    {entry.serviceTypeName} · booked for {formatDay(entry.jobScheduledDate)} at{' '}
                    {formatWallTime(entry.jobScheduledTime)}
                  </p>
                  {entry.kind === 'reschedule' && entry.requestedDate ? (
                    <p className="mt-1 text-body text-ink-900">
                      They asked for {formatDay(entry.requestedDate)}
                      {entry.requestedTime ? ` at ${formatWallTime(entry.requestedTime)}` : ''}
                      {entry.requestedTime ? '' : ' — same time'}
                    </p>
                  ) : null}
                  {entry.notes ? (
                    <p className="mt-2 text-meta text-ink-700">They said: {entry.notes}</p>
                  ) : null}
                  {entry.reviewNote ? (
                    <p className="mt-2 rounded-sm border border-ink-300 bg-honey-50 px-3 py-2 text-meta text-bark-900">
                      Sent to the client
                      {entry.reviewedByName ? ` by ${entry.reviewedByName}` : ''}:{' '}
                      {entry.reviewNote}
                    </p>
                  ) : null}
                </div>

                {entry.status === 'pending' ? (
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <Button
                      onClick={() => {
                        setReviewing({ request: entry, action: 'approve' });
                      }}
                    >
                      Confirm
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setReviewing({ request: entry, action: 'decline' });
                      }}
                    >
                      Decline
                    </Button>
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <ReviewDialog
        reviewing={reviewing}
        onClose={() => {
          setReviewing(null);
        }}
      />
    </section>
  );
}

function ReviewDialog({
  reviewing,
  onClose,
}: {
  reviewing: { request: AdminChangeRequestView; action: 'approve' | 'decline' } | null;
  onClose: () => void;
}) {
  return (
    <Dialog.Root
      open={reviewing !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          {reviewing ? (
            <ReviewForm request={reviewing.request} action={reviewing.action} onDone={onClose} />
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ReviewForm({
  request,
  action,
  onDone,
}: {
  request: AdminChangeRequestView;
  action: 'approve' | 'decline';
  onDone: () => void;
}) {
  const toast = useToast();
  const approve = useApproveChangeRequest(request.id);
  const decline = useDeclineChangeRequest(request.id);
  const [reviewNote, setReviewNote] = useState('');

  const mutation = action === 'approve' ? approve : decline;
  const error = mutation.error instanceof ApiError ? mutation.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  function submit(event: FormEvent) {
    event.preventDefault();

    if (action === 'approve') {
      approve.mutate(
        { reviewNote },
        {
          onSuccess: () => {
            toast.success(
              request.kind === 'cancellation'
                ? `${request.jobNumber} is cancelled, and the client has been told.`
                : `${request.jobNumber} has moved, and the client has been told.`,
            );
            onDone();
          },
        },
      );
      return;
    }

    decline.mutate(
      { reviewNote },
      {
        onSuccess: () => {
          toast.success('Declined, and the client has been told why.');
          onDone();
        },
      },
    );
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">
        {action === 'approve'
          ? request.kind === 'cancellation'
            ? 'Cancel this clean'
            : 'Move this clean'
          : 'Decline this request'}
      </Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        {request.clientName} · {request.jobNumber} · {request.addressSnapshot}
      </Dialog.Description>

      {action === 'approve' ? (
        <p className="mt-4 rounded-sm border border-ink-300 bg-ink-100/40 px-3 py-2.5 text-meta text-ink-700">
          {request.kind === 'cancellation'
            ? 'Confirming cancels the job. Cancellation is final — a mistaken one is a new job, not an edit.'
            : `Confirming moves the job to ${request.requestedDate ? formatDay(request.requestedDate) : 'the requested day'}${request.requestedTime ? ` at ${formatWallTime(request.requestedTime)}` : ' at the same time'}.`}
        </p>
      ) : null}

      <form onSubmit={submit} className="mt-5 space-y-4">
        {error && Object.keys(fieldErrors).length === 0 ? (
          <p
            role="alert"
            className="rounded-sm bg-red-50 px-3 py-2.5 text-body text-status-priority-text"
          >
            {error.message}
          </p>
        ) : null}

        <div className="space-y-1.5">
          <Label htmlFor="change-review-note" required={action === 'decline'}>
            {action === 'approve' ? 'Note back to the client' : 'Why'}
          </Label>
          {/* P20 — nothing in the prototype's admin UI says the client reads this. */}
          <p className="flex items-start gap-2 rounded-sm border border-honey-500 bg-honey-50 px-3 py-2.5 text-meta text-bark-900">
            <Eye aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            <span>
              <span className="font-medium">The client reads this, word for word.</span>
            </span>
          </p>
          <textarea
            id="change-review-note"
            name="reviewNote"
            rows={3}
            required={action === 'decline'}
            value={reviewNote}
            onChange={(event) => {
              setReviewNote(event.target.value);
            }}
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
            placeholder={
              action === 'approve'
                ? 'Moved to the Thursday — see you at nine.'
                : 'We are fully booked that week — could we do the Monday after?'
            }
          />
          {fieldErrors.reviewNote ? (
            <p role="alert" className="text-meta text-status-priority-text">
              {fieldErrors.reviewNote}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <Dialog.Close asChild>
            <Button variant="outline" disabled={mutation.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            type="submit"
            variant={action === 'decline' ? 'danger' : 'primary'}
            disabled={mutation.isPending}
          >
            {mutation.isPending
              ? 'Saving…'
              : action === 'approve'
                ? request.kind === 'cancellation'
                  ? 'Cancel the job'
                  : 'Move the job'
                : 'Decline request'}
          </Button>
        </div>
      </form>
    </>
  );
}
